<script setup lang="ts">
import { useQuasar } from 'quasar';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';

import HomepageItemSelectionModal from '@/components/HomepageItemSelectionModal.vue';
import { Constants } from '@/constants';
import { useConsultationsStore, useProjectStore } from '@/stores';
import { getTranslation } from '@/composables/data-display-utils';
import type { Locales } from '@/services/types/api.types';

const { locale, t } = useI18n();
const $q = useQuasar();

// PROPS
const props = defineProps<{
  disabled?: boolean;
  hint?: string;
  itemType: number | null;
  value: string;
  languageEnabled: Locales[];
}>();

// STORES
const consultationsStore = useConsultationsStore();
const projectStore = useProjectStore();

// EMITS
const emit = defineEmits(['clear', 'selection']);

// REFS
const hint = ref<string | undefined>(props.hint);
const title = ref<string | undefined>();

// FIELDS
const { value } = useField<number | null>(props.value);

// LIFECYCLE
onMounted(async () => {
  updateHint();
  updateTitle();
});

// FUNCTIONS
async function openModal() {
  if (!props.itemType) return;
  const modalTitle = (() => {
    switch (props.itemType) {
      case Constants.HomepageItem.Type.Consultation:
        return t('homepage-item-selection.modal.title.consultation');
      case Constants.HomepageItem.Type.Project:
        return t('homepage-item-selection.modal.title.project');
    }
  })();
  $q.dialog({
    component: HomepageItemSelectionModal,
    componentProps: { itemType: props.itemType, languageEnabled: props.languageEnabled, title: modalTitle },
  }).onOk((row) => {
    title.value = getTranslation(row, 'title', props.languageEnabled);
    emit('selection', row);
  });
}

function updateHint(itemType: number | null = props.itemType) {
  switch (itemType) {
    case Constants.HomepageItem.Type.Consultation:
      hint.value = t('homepage-item-selection.label.search-consultation');
      break;
    case Constants.HomepageItem.Type.Project:
      hint.value = t('homepage-item-selection.label.search-project');
      break;
    default:
      hint.value = props.hint;
      break;
  }
}

async function updateTitle() {
  if (!props.itemType || !value.value) {
    title.value = undefined;
  } else if (props.itemType === Constants.HomepageItem.Type.Consultation) {
    await consultationsStore.fetchConsultation(value.value);
    title.value = getTranslation(consultationsStore.getConsultation(value.value), 'title', props.languageEnabled);
  } else if (props.itemType === Constants.HomepageItem.Type.Project) {
    await projectStore.fetchById(value.value);
    title.value = getTranslation(projectStore.getItem(value.value), 'title', props.languageEnabled);
  }
}

// WATCH
watch(() => props.itemType, updateHint);
watch(
  () => locale.value,
  () => {
    updateHint();
    updateTitle();
  },
);
watch(
  () => value.value,
  (newValue: number | null, oldValue: number | null) => {
    if (newValue !== oldValue) {
      updateTitle();
    }
  },
);
</script>
<template>
  <div class="item-selection-button">
    <q-input
      outlined
      v-model="title"
      :label="hint"
      clearable
      @keyup.backspace="emit('clear')"
      @keydown.prevent
      :disable="disabled"
      @click="openModal"
      @clear="emit('clear')"
    />
  </div>
</template>
